import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
// material
import { Stack, TextField, Button, Grid, MenuItem } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import BackDropLoader from '../../../components/BackDropLoader';
import { getNewDate } from '../../../utils';
import { useAuth } from '../../../contexts/authContext';
import useDateTimePicker from '../../../hooks/useDateTimePicker';
import { add } from 'date-fns';
import { getTrainingsLocations } from '../services/trainingsService';
import React, { useState, useEffect } from 'react';

// ----------------------------------------------------------------------

export default function TrainingForm({ training, onSubmit, editOn = true, onCancel }) {
  const intl = useIntl();
  const existingTraining = Boolean(training);
  const auth = useAuth();
  const DateTimePicker = useDateTimePicker();

  const dateNow = getNewDate();
  const minDateFrom = add(dateNow, { hours: 1 });
  const minDateTo = add(minDateFrom, { minutes: 30 });
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const locationsData = await getTrainingsLocations(auth.user.id);
        setLocations(locationsData);
      } catch (error) {
        console.error('Fetch locations failed', error.message);
      }
    };
  
    fetchLocations();
  }, []);

  const TrainingSchema = Yup.object().shape({
    id: Yup.number(),
    name: Yup.string().min(1, 'common.validation.string.minMax').max(50, 'common.validation.string.minMax').required('common.validation.required'),
    availableSpots: Yup.number().required('common.validation.required').min(0, 'common.validation.number.positive'),
    description: Yup.string().required('common.validation.required').min(1, 'common.validation.description.minMax').max(250, 'common.validation.description.minMax'),
    reservationTimeFrom: Yup.date().nullable(true).required('common.validation.required').min(minDateFrom, 'common.validation.date.min'),
    reservationTimeTo: Yup.date().nullable(true).required('common.validation.required').min(minDateTo, 'common.validation.date.min'),
    creatorId: Yup.number(),
    locationId: Yup.number().required('common.validation.required')
  });


  const formik = useFormik({
    initialValues: {
      id: training ? training.id : 0,
      name: training ? training.name : '',
      availableSpots: training ? training.availableSpots : 10,
      description: training ? training.description : 'Training',
      reservationTimeFrom: training ? training.reservationTimeFrom : add(minDateFrom, { minutes: 30 }),
      reservationTimeTo: training ? training.reservationTimeTo : add(minDateTo, { minutes: 30 }),
      creatorId: training ? training.creatorId : auth.user.id,
      locationId: training ? training.locationId : (locations.length > 0 ? locations[0].id : ''),
    },
    validationSchema: TrainingSchema,
    onSubmit
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, dirty, setFieldValue, isValid, resetForm, setTouched, values, setValues } = formik;

  const handleCancel = () => {
    resetForm();
    if (onCancel) onCancel();
  };

  return (
    <FormikProvider value={formik}>
      <BackDropLoader open={isSubmitting} />
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              required
              fullWidth
              disabled={!editOn}
              label={intl.formatMessage({
                id: 'training.form.label.name',
                defaultMessage: 'Name'
              })}
              {...getFieldProps('name')}
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name && errors.name && <FormattedMessage id={formik.errors.name} defaultMessage={formik.errors.name} />}
            />

            <TextField
              type='number'
              required
              fullWidth
              disabled={!editOn}
              label={intl.formatMessage({
                id: 'training.form.label.availableSpots',
                defaultMessage: 'Available Spots'
              })}
              {...getFieldProps('availableSpots')}
              error={Boolean(touched.availableSpots && errors.availableSpots)}
              helperText={touched.availableSpots && errors.availableSpots && <FormattedMessage id={formik.errors.availableSpots} defaultMessage={formik.errors.availableSpots} />}
              InputProps={{ inputProps: { min: 0 } }}
            />

            <TextField
              required
              fullWidth
              disabled={!editOn}
              label={intl.formatMessage({
                id: 'training.form.label.description',
                defaultMessage: 'Description'
              })}
              {...getFieldProps('description')}
              error={Boolean(touched.description && errors.description)}
              helperText={touched.description && errors.description && <FormattedMessage id={formik.errors.description} defaultMessage={formik.errors.description} />}
            />
            
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>

            <DateTimePicker
              key={'reservationTimeFrom'}
              label={intl.formatMessage({
                id: 'training.form.label.reservationTimeFrom',
                defaultMessage: 'Reservation Time From'
              })}
              value={values.reservationTimeFrom}
              onChange={(value) => {
                setTouched({ ...touched, reservationTimeFrom: true });
                setValues({ ...values, reservationTimeFrom: value, reservationTimeTo: add(value, { hours: 1 }) });
              }}
              disabled={!editOn}
              disablePast={true}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    error={Boolean(touched.reservationTimeFrom && errors.reservationTimeFrom)}
                    helperText={touched.reservationTimeFrom && errors.reservationTimeFrom && <FormattedMessage id={formik.errors.reservationTimeFrom} defaultMessage={formik.errors.reservationTimeFrom} />}
                  />);
              }}
            />

            <DateTimePicker
              key={'reservationTimeTo'}
              label={intl.formatMessage({
                id: 'training.form.label.reservationTimeTo',
                defaultMessage: 'Reservation Time To'
              })}
              value={values.reservationTimeTo}
              onChange={(value) => {
                setTouched({ ...touched, reservationTimeTo: true });
                setFieldValue('reservationTimeTo', value, true);
              }}
              disabled={!editOn}
              disablePast={true}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    error={Boolean(touched.reservationTimeTo && errors.reservationTimeTo)}
                    helperText={touched.reservationTimeTo && errors.reservationTimeTo && <FormattedMessage id={formik.errors.reservationTimeTo} defaultMessage={formik.errors.reservationTimeTo} />}
                  />);
              }}
            />
          </Stack>

          <Stack spacing={3}>
            <TextField
              select
              required
              fullWidth
              disabled={!editOn}
              label={intl.formatMessage({
                id: 'training.form.label.location',
                defaultMessage: 'Location'
              })}
              {...getFieldProps('locationId')}
              value={values.locationId}
              error={Boolean(touched.locationId && errors.locationId)}
              helperText={touched.locationId && errors.locationId && <FormattedMessage id={formik.errors.locationId} defaultMessage={formik.errors.locationId} />}
            >
              {locations.map(location => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {
            editOn &&
            <Grid container justifyContent="flex-end" sx={{ paddingTop: 0 }}>
              <Button onClick={handleCancel} disabled={isSubmitting}>
                <FormattedMessage id='common.button.cancel' defaultMessage='Cancel' />
              </Button>
              <LoadingButton type="submit" loading={isSubmitting} disabled={!dirty || !isValid} >
                <FormattedMessage id={`common.button.${existingTraining ? 'update' : 'create'}`} defaultMessage='Create/Update' />
              </LoadingButton>
            </Grid>
          }
        </Stack>
      </Form>
    </FormikProvider>
  );
}

TrainingForm.propTypes = {
  training: PropTypes.any,
  onSubmit: PropTypes.func.isRequired,
  editOn: PropTypes.bool,
  onCancel: PropTypes.func
};
